import type { IModalInstanceService, IModalService, IModalSettings } from 'angular-ui-bootstrap';
import { FailedProcessReportType } from 'src/AngularJS/Admin/PipelineConfiguration/ProcessReports/failed-process-reports-modal.component';
import { SelectRecalcResolves } from 'src/AngularJS/Admin/PipelineConfiguration/Recalculation/select-recalc-modal.component';
import { SimulatePipelineResolves } from 'src/AngularJS/Admin/PipelineConfiguration/simulate-result.modal';
import { ClearViewUtils } from 'src/shared/utils';

export default class PipelineConfigurationModalsServiceBase {

    private $uibModal: IModalService;

    constructor(
        fromSharedService: boolean,
    ) {
        if (fromSharedService)
            return PipelineConfigurationModalsServiceBase.getServiceBase();
    }

    static getServiceBase(): PipelineConfigurationModalsServiceBase {
        return ClearViewUtils.getSharedService(PipelineConfigurationModalsServiceBase, 'PipelineConfigurationModalsServiceBase');
    }

    public setUibModalService($uibModal: IModalService) {
        this.$uibModal = $uibModal;
    }

    /**
     * Creates and returns the modal settings for Failed Process Reports Modal
     * @param type Type of Failed Process Reports to display
     */
    public createFailedProcessModalSettings(type: FailedProcessReportType): IModalSettings {
        if (type != FailedProcessReportType.RawData && type != FailedProcessReportType.Hierarchy && type != FailedProcessReportType.ReportingSet)
            throw "Invalid Failed Process Report Type";

        return {
            backdrop: "static",
            component: "failed-process-reports-modal",
            size: "lg modal-dialog-centered",
            resolve: {
                type: () => type,
            }
        };
    }

    /**
     * Displays the Recalculation Selection modal.
     * @param preselected Optional IDs to default selected for recalculation.
     * @returns ModalInstance from the $uibModal.open call
     */
    public showRecalculationSelectionModal(preselected?: { metricIds?: number[], reportingSetIds?: string[], hierarchyIds?: string[] }): IModalInstanceService {
        const resolves: SelectRecalcResolves = {};
        if (preselected?.metricIds?.length > 0)
            resolves.metricIds = () => preselected.metricIds;
        if (preselected?.reportingSetIds?.length > 0)
            resolves.reportingSetIds = () => preselected.reportingSetIds;
        if (preselected?.hierarchyIds?.length > 0)
            resolves.hierarchyIds = () => preselected.hierarchyIds;

        return this.$uibModal.open({
            backdrop: "static",
            component: "select-recalc-modal",
            size: "lg modal-dialog-centered",
            resolve: resolves as any,
        });
    }

    /**
     * Opens the modal that displays simulated Reporting Set data.
     * @param id The _id value of the Reporting Set Definition
     * @param displayName The string to display as subtitle in the modal, until the data loads.
     */
    public openSimulateReportingSetModal(id: string, displayName?: string): IModalInstanceService {
        const resolves: SimulatePipelineResolves = {};
        resolves.definitionType = () => "reportingSet";
        resolves.definitionId = () => id;
        if (displayName)
            resolves.definitionName = () => displayName;

        return this.openSimulateModal(resolves);
    }

    /**
     * Opens the modal that displays a preview of Raw Data.
     * @param id The _id value of the Raw Data Definition
     * @param displayName The string to display as subtitle in the modal, until the data loads.
     */
    public openPreviewRawDataModal(id: string, displayName?: string): IModalInstanceService {
        const resolves: SimulatePipelineResolves = {};
        resolves.definitionType = () => "rawData";
        resolves.definitionId = () => id;
        if (displayName)
            resolves.definitionName = () => displayName;

        return this.openSimulateModal(resolves);
    }

    /**
     * Private function that constructs the UI Bootstrap Modal for Simulating (or Previewing) Pipeline Definitions
     * @param resolves Values passed into the modal.
     */
    private openSimulateModal(resolves: SimulatePipelineResolves): IModalInstanceService {
        return this.$uibModal.open({
            backdrop: "static",
            component: "simulate-result-modal",
            size: "lg modal-dialog-centered",
            resolve: resolves as any,
        });
    }
}
